/**
 * Explorer analytics utility.
 *
 * All events flow through window.dataLayer so they work whether GA4 is loaded
 * directly or via GTM (GTM-MKPN7RVN fires the GA4 tag GT-MMKPN7RVN).
 *
 * Usage:
 *   import { trackSearch, trackExport, trackLogin } from '../lib/analytics';
 */

// Pendo rejects undefined property values, so drop them before sending.
const trackPendoEvent = (name, props) => {
  if (!window.pendo?.isReady?.()) return;
  const definedProps = Object.entries(props).filter(([, value]) => value !== undefined);
  window.pendo.track?.(name, Object.fromEntries(definedProps));
};

/** Push an event object to GTM dataLayer (and therefore GA4). */
function push(payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * Hash a string with SHA-256 using the Web Crypto API.
 * Returns a hex string, or the original value if crypto is unavailable.
 */
async function sha256(value) {
  if (!value) return "";
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(String(value).trim().toLowerCase());
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    // SubtleCrypto unavailable (HTTP or very old browser) — return as-is.
    return String(value);
  }
}

// ── Login ─────────────────────────────────────────────────────────────────────

/**
 * Fire once per session after the user authenticates.
 *
 * @param {object} user   Auth0 user object
 */
export async function trackLogin(user) {
  if (!user) return;
  const userId = await sha256(user.sub);
  const emailDomain = user.email ? user.email.split("@")[1] : undefined;

  // Set user_id on the GA4 config so every subsequent hit carries it.
  if (typeof window.gtag === "function") {
    window.gtag("set", "user_properties", {
      user_id: userId,
      email_domain: emailDomain,
    });
  }

  push({
    event: "user_authenticated",
    userId,
    userEmailDomain: emailDomain,
    userName: user.name,
  });
}

// ── Search ────────────────────────────────────────────────────────────────────

/**
 * Fire when a search query is submitted.
 *
 * @param {string} query        Natural-language query the user typed
 * @param {number} resultCount  Number of results returned (pass 0 if unknown)
 * @param {object} [params]     Parsed parameters from /api/search/parameters
 */
export function trackSearch(query, resultCount = 0, params = {}) {
  if (!query) return;

  // Derive the broadest geography level from the parsed parameters.
  const geoLevel = params?.geo_level || deriveGeoLevel(params);

  push({
    event: "explorer_search",
    searchQuery: query,
    searchResultCount: resultCount,
    searchGeoLevel: geoLevel,
  });
}

/**
 * Infer a geography label from the search parameters object.
 * Adjust the field names to match what /api/search/parameters actually returns.
 */
function deriveGeoLevel(params = {}) {
  if (!params || typeof params !== "object") return "unknown";
  if (params.state && params.congressional_district) return "congressional_district";
  if (params.state && params.state_senate_district) return "state_senate";
  if (params.state && params.state_house_district) return "state_house";
  if (params.state && (params.city || params.county)) return "local";
  if (params.state) return "state";
  return "national";
}

// ── Export ────────────────────────────────────────────────────────────────────

/**
 * Fire when the user downloads data.
 *
 * @param {string} fileType     'csv' | 'png'
 * @param {string} fileName     Downloaded file name
 * @param {number} [recordCount] Row count (CSV only)
 */
export function trackExport(fileType, fileName, recordCount) {
  push({
    event: "explorer_export",
    exportFileType: fileType,
    exportFileName: fileName,
    exportRecordCount: recordCount ?? undefined,
  });
}

// ── Feature engagement ────────────────────────────────────────────────────────

/**
 * Generic feature interaction event.
 *
 * @param {string} featureName  e.g. 'map', 'bar_chart', 'pie_chart', 'table'
 * @param {string} action       e.g. 'view', 'expand', 'filter_change'
 */
export function trackFeature(featureName, action) {
  push({
    event: "explorer_feature",
    featureName,
    featureAction: action,
  });
}

// ── Generic event (GA4 via dataLayer + Pendo) ─────────────────────────────────

/**
 * Fire a semantic product event to both GA4 and Pendo.
 * Props must be small enums/counts -- never query text or place names.
 *
 * @param {string} name   snake_case `<area>_<object>_<verb>`
 * @param {object} [props]
 */
export function trackEvent(name, props = {}) {
  push({ event: name, ...props });
  trackPendoEvent(name, props);
}
